import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { ApiError, fetchFastApi } from "../api/fastapi";
import { useAudit } from "./auditContext";
import { useHookCache } from "../lib/hookCache";
import { sessionEnvelopeCacheKey } from "../hooks/audit/useControlSessionEnvelope";
import { controlsV2SessionApiBase } from "../lib/sessionApiBase";
import { pollJob } from "../lib/pollJob";
import {
  describeSendJob,
  isStartUnconfirmed,
  SEND_UNCONFIRMED_COPY,
  START_UNCONFIRMED_COPY,
  STATUS_UNAVAILABLE_COPY,
  startRefusalMessage,
} from "../lib/optroSendMessages";
import {
  isArtifactLanded,
  OptroSendJobStatus,
  OptroSendJobStatusSchema,
  StartOptroSendResponseSchema,
} from "../models/audit/optroSend";

// Fast while a typical send finishes, then slow: one that outlives the fast
// window is a long one, and an hour at 3s would be 1,200 requests.
const FAST_POLL_INTERVAL_MS = 3000;
const SLOW_POLL_INTERVAL_MS = 10000;
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;

// The send job allows 30 minutes queued plus 30 running (the backend's
// SEND_SCHEDULE_TIMEOUT and SEND_EXECUTION_TIMEOUT); the extra minute keeps the
// client from giving up on a run the server still honours. A poll that reaches
// it can't say how the run ended, only that it has.
const SEND_DEADLINE_MS = 61 * 60 * 1000;

interface OptroSendContextValue {
  sendingSessionIds: ReadonlySet<string>;
  // `entityName` is the session's control or work step, named in the result
  // toast; null leaves the toast unnamed.
  send: (
    sessionId: string,
    templateId: string,
    entityName: string | null,
  ) => Promise<void>;
}

const OptroSendContext = createContext<OptroSendContextValue | null>(null);

// fastapi.ts answers a dead Optro credential, or a token refresh that failed,
// by clearing the session and loading /signin. A toast would only flash on the
// way out.
const signedOutByRequest = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status === 401 &&
  localStorage.getItem("access_token") === null;

// Results toast wherever the user is by then, so each names its session: the
// name on its own line, then the message. One wrapping element, because the
// toast title lays its children out side by side.
function forSendEntity(entityName: string | null, message: string): ReactNode {
  const name = entityName?.trim();
  if (!name) return message;
  return (
    <span className="flex flex-col">
      <strong>{name}</strong>
      <span>{message}</span>
    </span>
  );
}

// Owns Send to Optro for the session shell, above the tabs, so a send in
// flight outlives the button, which unmounts when the user leaves the
// Workpaper Export tab, and a remounted button still reads "Sending". The poll
// runs to the end wherever the user goes, so the result always toasts, named
// for its session. Leaving the session pages drops the "Sending" state with
// the shell; the backend refuses a second send while one runs.
export function OptroSendProvider({ children }: { children: ReactNode }) {
  const { sessionKind } = useAudit();
  const { invalidate } = useHookCache();
  const [sendingSessionIds, setSendingSessionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  // Read synchronously, so a double click cannot start two sends.
  const inFlightRef = useRef(new Set<string>());
  // Sessions whose last send landed the workpaper but not the evidence
  // bundle, so the send that completes them can say so. Not persisted.
  const partialSendRef = useRef(new Set<string>());
  // An unmounted shell takes its cache with it: nothing is left to refresh.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setSending = useCallback((sessionId: string, sending: boolean) => {
    if (sending) {
      inFlightRef.current.add(sessionId);
    } else {
      inFlightRef.current.delete(sessionId);
    }
    setSendingSessionIds(new Set(inFlightRef.current));
  }, []);

  const refreshLastSent = useCallback(
    async (sessionId: string) => {
      if (!mountedRef.current) return;
      await invalidate(sessionEnvelopeCacheKey(sessionId, sessionKind));
    },
    [invalidate, sessionKind],
  );

  const toastJobResult = useCallback(
    (sessionId: string, entityName: string | null, job: OptroSendJobStatus) => {
      let completesPartialSend = false;
      if (job.status === "success" && job.outcome) {
        const { workpaper, evidence } = job.outcome;
        if (isArtifactLanded(workpaper) && isArtifactLanded(evidence)) {
          completesPartialSend = partialSendRef.current.has(sessionId);
          partialSendRef.current.delete(sessionId);
        } else if (isArtifactLanded(workpaper)) {
          partialSendRef.current.add(sessionId);
        }
      }

      const { variant, message, description } = describeSendJob(
        job,
        completesPartialSend,
      );
      const title = forSendEntity(entityName, message);
      if (variant === "success") {
        toast.success(title);
      } else {
        toast.error(title, { description });
      }
    },
    [],
  );

  const send = useCallback(
    async (
      sessionId: string,
      templateId: string,
      entityName: string | null,
    ) => {
      if (inFlightRef.current.has(sessionId)) return;
      setSending(sessionId, true);

      try {
        // The template the export tab shows, built into the path:
        // fetchFastApi applies `query` to GETs only.
        const path = `${controlsV2SessionApiBase(sessionKind, sessionId)}/send-to-optro?template_id=${encodeURIComponent(templateId)}`;

        let taskId: string;
        try {
          const response = await fetchFastApi({
            path,
            method: "POST",
            throwOnErrorEnvelope: true,
          });
          taskId = StartOptroSendResponseSchema.parse(response).task_id;
        } catch (error) {
          if (signedOutByRequest(error)) return;
          console.error("Error starting send to Optro:", error);
          if (isStartUnconfirmed(error)) {
            toast.info(forSendEntity(entityName, START_UNCONFIRMED_COPY));
            // The copy points at the last-sent time, so make it current.
            await refreshLastSent(sessionId);
            return;
          }
          toast.error(forSendEntity(entityName, startRefusalMessage(error)));
          return;
        }

        const result = await pollJob({
          path: `/sessions/optro-send-job/${encodeURIComponent(taskId)}`,
          schema: OptroSendJobStatusSchema,
          isTerminal: (job) =>
            job.status === "success" || job.status === "failure",
          intervalMs: (elapsedMs) =>
            elapsedMs < FAST_POLL_WINDOW_MS
              ? FAST_POLL_INTERVAL_MS
              : SLOW_POLL_INTERVAL_MS,
          deadlineMs: SEND_DEADLINE_MS,
          // Rides out a short outage before giving up on a run that can last
          // an hour.
          backoffOnFailure: true,
          // The poll outlives the page, so signing out has to end it, or it
          // would fail on and toast over the sign-in page.
          isCancelled: () => localStorage.getItem("access_token") === null,
        });

        switch (result.kind) {
          case "done":
            toastJobResult(sessionId, entityName, result.status);
            // The Resend label reads optroLastSentAt off the envelope. Awaited
            // so the button goes from "Sending" straight to its new label.
            await refreshLastSent(sessionId);
            return;
          case "timed_out":
            toast.info(forSendEntity(entityName, SEND_UNCONFIRMED_COPY));
            // The copy points at the last-sent time, so make it current.
            await refreshLastSent(sessionId);
            return;
          case "failed":
            toast.error(forSendEntity(entityName, STATUS_UNAVAILABLE_COPY));
            await refreshLastSent(sessionId);
            return;
          // Only signing out cancels this poll.
          case "cancelled":
            return;
          default: {
            const unreachable: never = result;
            return unreachable;
          }
        }
      } finally {
        setSending(sessionId, false);
      }
    },
    [refreshLastSent, sessionKind, setSending, toastJobResult],
  );

  const value = useMemo(
    () => ({ sendingSessionIds, send }),
    [sendingSessionIds, send],
  );

  return (
    <OptroSendContext.Provider value={value}>
      {children}
    </OptroSendContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptroSend(): OptroSendContextValue {
  const context = useContext(OptroSendContext);
  if (!context) {
    throw new Error("useOptroSend must be used within OptroSendProvider");
  }
  return context;
}
